import config from '@src/config'
import type { PhoneNumberId } from '@src/service/model'
import type { RawWorkflowDefinitionCriteriaType } from '@src/service/model/workflow/WorkflowDefinitionCriteriaTypeModel'
import type { RawWorkflowDefinition } from '@src/service/model/workflow/WorkflowDefinitionModel'
import type { RawWorkflowFilterDefinition } from '@src/service/model/workflow/WorkflowFilterDefinitionModel'
import type { RawWorkflowScheduleDefinition } from '@src/service/model/workflow/WorkflowScheduleDefinitionModel'
import type { RawWorkflowStepDefinition } from '@src/service/model/workflow/WorkflowStepDefinitionModel'
import type { RawWorkflowTriggerDefinition } from '@src/service/model/workflow/WorkflowTriggerDefinitionModel'

import type Transport from '.'
import type Paginated from './lib/Paginated'
import { HttpTransaction } from './transaction'

/**
 * Error codes that are returned from the workflow service API
 */
type ServerValidationErrorCode =
  | 'CAN_NOT_COME_ANYWHERE_AFTER_STEP'
  | 'CANNOT_FORWARD_TO_INTERNATIONAL'
  | 'CANNOT_FORWARD_TO_SELF'
  | 'CONFLICTING_SCHEDULE'
  | 'ENABLED_CHANGED'
  | 'INITIAL_STEP_CANNOT_COME_AFTER_TRIGGER'
  | 'INSUFFICIENT_CAPABILITIES'
  | 'INTERNAL_TAGS_CHANGED'
  | 'INVALID_ARRAY'
  | 'INVALID_BOOLEAN'
  | 'INVALID_BRANCH_STEP'
  | 'INVALID_CATEGORY'
  | 'INVALID_CONFIGURATION' // Legacy
  | 'INVALID_ENABLED_STATE_FOR_PREVIEW'
  | 'INVALID_ENABLED_STATE'
  | 'INVALID_INITIAL_STEP'
  | 'INVALID_NUMBER'
  | 'INVALID_OBJECT'
  | 'INVALID_REQUIRED_CONFIGURATION' // Legacy
  | 'INVALID_SCHEDULED_FOR'
  | 'INVALID_STEP_DEFINITION'
  | 'INVALID_STEP_LOOP'
  | 'INVALID_STEP_REF_BRANCH'
  | 'INVALID_STRING'
  | 'INVALID_TRIGGER_DEFINITION'
  | 'INVALID_TYPE_FORMAT'
  | 'INVALID_USER_VARIABLE_REFERENCE'
  | 'INVALID_VARIABLE_REFERENCE'
  | 'INVALID_WORKFLOW_DEFINITION'
  | 'MISSING_CONDITION_FILTER'
  | 'MISSING_INPUT'
  | 'MISSING_PREVIOUS_STEP'
  | 'MISSING_REQUIRED_BRANCH'
  | 'MISSING_REQUIRED_CONFIGURATION'
  | 'STEPS_AFTER_TERMINAL_STEP'
  | 'TOO_MANY_INSTANCES_IN_PATH'
  | 'TYPE_MISMATCH'
  | 'UNKNOWN_TYPE'
  | 'UNREACHABLE_STEP'
  | 'UNSUPPORTED_STEP'
  | 'VALIDATION_COMMAND_FAILED'

/**
 * Error codes that are generated on the client based on Zod schema parsing
 */
type ClientValidationErrorCode =
  | 'MISSING_RING_ORDER_SINGLE_USER_ID'
  | 'MISSING_RING_ORDER_CUSTOM_GROUPS'
  | 'MISSING_RING_ORDER_CUSTOM_GROUP_USER_ID'

export type ValidationErrorCode = ServerValidationErrorCode | ClientValidationErrorCode

export interface Validation {
  valid: boolean
  errors: {
    /**
     * @deprecated This property is deprecated and will be removed in a future version.
     * @see https://linear.app/openphone/issue/WXP-84/remove-path-property-from-validation
     */
    path: string
    message: string
    code: ValidationErrorCode
    details?: {
      code: ValidationErrorCode
      property: string
      value: string
      stepId?: string
    }
  }[]
}

export type CreateWorkflowDefinitionParams = Pick<
  RawWorkflowDefinition,
  | 'emoji'
  | 'name'
  | 'description'
  | 'entityId'
  | 'orgId'
  | 'internalTags'
  | 'enabled'
  | 'trigger'
  | 'initialStepId'
  | 'workflowSteps'
  | 'configuration'
  | 'isEphemeral'
  | 'isPreview'
  | 'template'
>

export type CreateWorkflowScheduleDefinitionParams = Pick<
  RawWorkflowScheduleDefinition,
  'orgId' | 'entityId' | 'trigger' | 'workflow' | 'scheduledFor' | 'configuration'
>

export type UpdateWorkflowScheduleDefinitionParams = Partial<
  Pick<
    RawWorkflowScheduleDefinition,
    'entityId' | 'trigger' | 'workflow' | 'scheduledFor' | 'configuration'
  >
>

export type SupportedWorkflowEntityId = PhoneNumberId

export default class WorkflowClient {
  constructor(private transport: Transport) {}

  definitions = {
    list: ({
      orgId,
      userId,
      maxResults,
      pageToken,
      entityIds,
      enabled,
    }: {
      orgId: string
      userId: string
      maxResults: number
      pageToken?: string
      entityIds?: SupportedWorkflowEntityId[]
      enabled?: boolean
    }): Promise<Paginated<RawWorkflowDefinition>> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-definitions`,
          query: {
            orgId,
            userId,
            maxResults,
            pageToken,
            entityIds: entityIds?.join(','),
            enabled,
          },
        }),
      )
    },

    post: (
      definition: CreateWorkflowDefinitionParams,
    ): Promise<RawWorkflowDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'POST',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-definitions`,
          body: definition,
        }),
      )
    },

    get: (id: string, version?: number): Promise<RawWorkflowDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-definitions/${id}`,
          query: { version },
        }),
      )
    },

    put: (
      id: string,
      definition: RawWorkflowDefinition,
    ): Promise<RawWorkflowDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'PUT',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-definitions/${id}`,
          body: definition,
        }),
      )
    },

    delete: (id: string): Promise<null> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'DELETE',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-definitions/${id}`,
        }),
      )
    },

    validate: (id: string, definition: RawWorkflowDefinition): Promise<Validation> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'POST',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-definitions/${id}/validate`,
          body: definition,
        }),
      )
    },

    enable: (id: string): Promise<RawWorkflowDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'POST',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-definitions/${id}/enable`,
        }),
      )
    },
  }

  filterDefinitions = {
    list: (
      maxResults: number,
      pageToken?: string,
      supportedOnly?: boolean,
    ): Promise<Paginated<RawWorkflowFilterDefinition>> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-filter-definitions`,
          query: {
            maxResults,
            pageToken,
            supportedOnly,
          },
        }),
      )
    },

    get: (id: string, version?: number): Promise<RawWorkflowFilterDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-filter-definitions/${id}`,
          query: { version },
        }),
      )
    },
  }

  criteriaTypes = {
    list: (
      maxResults: number,
      pageToken?: string,
    ): Promise<Paginated<RawWorkflowDefinitionCriteriaType>> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-condition-definitions`,
          query: {
            maxResults,
            pageToken,
          },
        }),
      )
    },
  }

  stepDefinitions = {
    list: (
      maxResults: number,
      pageToken?: string,
    ): Promise<Paginated<RawWorkflowStepDefinition>> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-step-definitions`,
          query: {
            maxResults,
            pageToken,
          },
        }),
      )
    },

    get: (id: string, version?: number): Promise<RawWorkflowStepDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-step-definitions/${id}`,
          query: { version },
        }),
      )
    },
  }

  triggerDefinitions = {
    list: (
      maxResults: number,
      pageToken?: string,
    ): Promise<Paginated<RawWorkflowTriggerDefinition>> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-trigger-definitions`,
          query: {
            maxResults,
            pageToken,
          },
        }),
      )
    },

    get: (id: string, version?: number): Promise<RawWorkflowTriggerDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-trigger-definitions/${id}`,
          query: { version },
        }),
      )
    },
  }

  schedules = {
    list: ({
      orgId,
      entityIds,
      triggerId,
      maxResults,
      pageToken,
    }: {
      orgId: string
      entityIds?: string[]
      triggerId?: string
      maxResults: number
      pageToken?: string
    }): Promise<Paginated<RawWorkflowScheduleDefinition>> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-schedule-definitions`,
          query: {
            orgId,
            entityIds: entityIds?.join(','),
            triggerId,
            maxResults,
            pageToken,
          },
        }),
      )
    },

    post: (
      schedule: CreateWorkflowScheduleDefinitionParams,
    ): Promise<RawWorkflowScheduleDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'POST',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-schedule-definitions`,
          body: schedule,
        }),
      )
    },

    get: (id: string, version?: number): Promise<RawWorkflowScheduleDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'GET',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-schedule-definitions/${id}`,
          query: { version },
        }),
      )
    },

    put: (
      id: string,
      schedule: UpdateWorkflowScheduleDefinitionParams,
    ): Promise<RawWorkflowScheduleDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'PUT',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-schedule-definitions/${id}`,
          body: schedule,
        }),
      )
    },

    delete: (id: string): Promise<null> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'DELETE',
          url: `${config.WORKFLOW_SERVICE_URL}workflow-schedule-definitions/${id}`,
        }),
      )
    },
  }
}
